import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faPenToSquare, faTrash, faUserPlus, faUsers } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Card, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ColumnInterface } from 'react-table'

import { ALL_DATA_LIMIT } from '@/api/constants'
import { useDeleteGroup } from '@/api/group/delete'
import { useGroup } from '@/api/group/get'
import { useRemoveMembersFromGroup } from '@/api/group/remove-members'
import { useGroups } from '@/api/groups/get'
import { useMembers } from '@/api/members/get'
import { useOffices } from '@/api/offices/get'
import removeImg from '@/assets/images/popup/delete.svg'
import CardHeader from '@/common/components/Card/CardHeader'
import GaugeScoremeter from '@/common/components/GaugeScoremeter/GaugeScoremeter'
import HumanizedScore from '@/common/components/HumanizedScore/HumanizedScore'
import LayoutPageContent from '@/common/components/Layout/LayoutPageContent'
import PageHeader from '@/common/components/Layout/LayoutPageContent/PageHeader'
import MoreMenu from '@/common/components/MoreMenu/MoreMenu'
import Popup from '@/common/components/Popup'
import SimpleSearchBar from '@/common/components/SimpleSearchBar/SimpleSearchBar'
import InTableMessageWithAnimation from '@/common/components/Tables/InTableMessageWithAnimation'
import { MultiselectBarProps } from '@/common/components/Tables/MultiselectBar'
import PaginatingTable from '@/common/components/Tables/PaginatingTable'
import useToast from '@/common/hooks/useToast'
import EditGroupModal from '@/pages/GroupProfile/EditGroupModal'
import { theme } from '@/theme/theme'
import { MembersListQuery } from '@/types/members'

const GroupProfile: FC = () => {
  const { t } = useTranslation()
  const navigateTo = useNavigate()
  const { successToast, errorToast } = useToast()
  const [editModalIsOpen, setEditModalIsOpen] = useState(false)
  const [editModalType, setEditModalType] = useState<'group' | 'users'>('group')
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
  const [groupForDeletion, setGroupForDeletion] = useState<string>('')
  const [usersForRemoval, setUsersForRemoval] = useState<string[]>([])
  const [removeUserManager, setRemoveUserManager] = useState<boolean>(false)
  const [removeUsersAlertIsOpen, setRemoveUsersAlertIsOpen] = useState(false)
  const theme = useTheme()
  const { _id } = useParams()
  const { data: group } = useGroup(_id || '')
  const { data: groupsData } = useGroups(ALL_DATA_LIMIT)
  const groups = groupsData?.results || []
  const { data: officesData } = useOffices(ALL_DATA_LIMIT)
  const offices = officesData?.results || []
  const { mutateAsync: deleteGroup } = useDeleteGroup()
  const { mutateAsync: removeMembersFromGroup } = useRemoveMembersFromGroup()
  const initalQueryFilter = { groups: [_id], skip: 0, limit: 5 }
  const [queryFilters, setQueryFilters] = useState(initalQueryFilter)
  const { data: members, isLoading, isFetching } = useMembers(queryFilters as MembersListQuery)
  const [pageCount, setPageCount] = useState(0)

  const fetchData = useCallback(
    ({ pageSize, pageIndex }: { pageSize: number; pageIndex: number }) => {
      setQueryFilters((prevState) => ({
        ...prevState,
        skip: prevState.limit * pageIndex,
        limit: pageSize,
      }))
      setPageCount(Math.ceil((members?.total || 0) / pageSize))
    },
    [members?.total]
  )

  function handleNavigateToMemberProfile({ original }: { original: any }) {
    navigateTo(`/recipients/users/${original._id}`)
  }

  const handleSearch = (value: string) => {
    setQueryFilters((prevFilters) => ({
      ...prevFilters,
      search: value,
    }))
  }

  function openRemoveUsersAlert(users: string[]) {
    setRemoveManager(users)
    setUsersForRemoval(users)
    setRemoveUsersAlertIsOpen(true)
  }

  const groupsById = useMemo(() => {
    const byId: { [key: string]: any } = {}
    if (groups) {
      groups.forEach((group) => {
        byId[group._id] = group
      })
    }
    return byId
  }, [groups])

  const columns: ColumnInterface[] = useMemo(
    () => [
      {
        Header: t('users.usersTable.name'),
        accessor: 'first_name',
        Cell: (data: any) => {
          const { first_name, last_name } = data.row.original
          return `${first_name} ${last_name}`
        },
      },
      {
        Header: t('users.usersTable.email'),
        accessor: 'username',
      },
      {
        Header: 'Phone Number',
        accessor: 'phone_number',
        Cell: ({ value }: { value: string }) => value || '-',
      },
      {
        Header: t('users.usersTable.groups'),
        accessor: 'groups',
        Cell: (data: any) => {
          const value = data.row.original.member_of
          const groupsByName: string[] = []
          if (Array.isArray(value) && value.length > 0) {
            value.forEach((groupId) => {
              const recipientGroup = groupsById[groupId]
              recipientGroup && groupsByName.push(recipientGroup.name)
            })
          }
          return groupsByName.length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                margin: 'auto',
              }}>
              <div
                style={{
                  borderRadius: '4px',
                  backgroundColor: '#ADEFF9',
                  padding: '4px 4px',
                  fontSize: '14px',
                }}>
                {groupsByName[0]}
              </div>
              {groupsByName.length > 1 ? (
                <Tooltip
                  placement={'right'}
                  title={groupsByName
                    .slice(1)
                    .map((group) => group)
                    .join(', ')}>
                  <div
                    style={{
                      padding: '3px 8px',
                      gap: '10px',
                      borderRadius: '20px',
                      border: '1px solid #000',
                      marginLeft: '5px',
                      verticalAlign: 'middle',
                    }}>
                    {`+${groupsByName.length - 1}`}
                  </div>
                </Tooltip>
              ) : null}
            </div>
          ) : (
            '-'
          )
        },
      },
      {
        Header: t('users.usersTable.office'),
        accessor: 'office_id',
        disableSortBy: true,
        Cell: ({ value }: { value: string }) => {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                margin: 'auto',
              }}>
              <div
                style={{
                  borderRadius: '4px',
                  backgroundColor: '#ADEFF9',
                  padding: '2px 4px',
                  fontSize: '14px',
                }}>
                {offices?.find((office) => office._id === value)?.name || ''}
              </div>
            </div>
          )
        },
      },
      {
        Header: t('users.usersTable.score'),
        accessor: 'awareness_score',
        Cell: ({ value }: { value: number }) => {
          return <HumanizedScore score={value} />
        },
      },
      {
        Header: t('users.usersTable.status'),
        accessor: 'state',
        Cell: ({ value }: { value: string }) =>
          value === 'active' ? t('users.usersTable.active') : t('users.usersTable.inactive'),
      },
      {
        id: 'delete',
        Cell: (data: any) => {
          const user = data.row.original
          const userId = user._id
          return (
            <MoreMenu
              items={[
                {
                  label: 'groupProfile.usersTable.remove',
                  textColor: theme.palette.error.main,
                  onClick: () => {
                    openRemoveUsersAlert([userId])
                  },
                },
              ]}
              ariaLabel={'groupProfile.usersTable.moreActions'}
            />
          )
        },
      },
    ],
    [t, offices]
  )

  function setRemoveManager(users: string[]) {
    group?.managers.forEach((manager) => {
      if (users.includes(manager._id)) {
        setRemoveUserManager(true)
        return
      }
    })
  }

  function closeRemoveUsersAlert() {
    setUsersForRemoval([])
    setRemoveUserManager(false)
    setRemoveUsersAlertIsOpen(false)
  }

  function closeDeleteGroupModal() {
    setGroupForDeletion('')
    setDeleteModalIsOpen(false)
  }

  async function handleRemoveUser() {
    try {
      await removeMembersFromGroup({ _id: _id ?? '', users: usersForRemoval })
      successToast(t('groupProfile.userDeleted'))
      closeRemoveUsersAlert()
    } catch (e) {
      errorToast(t('groupProfile.errors.faliedDeleteUser'))
    }
  }

  async function handleDeleteGroup() {
    try {
      await deleteGroup(groupForDeletion)
      successToast(t('users.groupsTable.groupDeleted'))
      setDeleteModalIsOpen(false)
      navigateTo(`/recipients/groups`)
    } catch (e) {
      errorToast(t('users.groupsTable.errors.faliedDeleteGroup'))
    }
  }

  async function handleDeleteGroupModalOpen() {
    setDeleteModalIsOpen(true)
    setGroupForDeletion(_id as string)
  }

  async function handleEditGroupModalOpen() {
    setEditModalType('group')
    setEditModalIsOpen(true)
  }

  async function handleAddGroupMembersModalOpen() {
    setEditModalType('users')
    setEditModalIsOpen(true)
  }

  const multiSelectActions: MultiselectBarProps['actions'] = [
    {
      label: 'groupProfile.usersTable.remove',
      onClick: (userIds) => {
        openRemoveUsersAlert(userIds)
      },
      icon: () => <FontAwesomeIcon icon={faTrash as IconProp} fontSize={20} />,
      variant: 'iconButton',
    },
  ]

  return (
    <LayoutPageContent>
      <Popup
        open={deleteModalIsOpen}
        onClose={closeDeleteGroupModal}
        onConfirm={handleDeleteGroup}
        buttonLabels={{
          confirm: t('groupProfile.removeModal.confirm'),
          cancel: t('groupProfile.removeModal.cancel'),
        }}
        icon={removeImg}>
        <h4>{t('groupProfile.removeModal.title')}</h4>
        <p style={{ fontSize: '16px' }}>{t('groupProfile.removeModal.description')}</p>
      </Popup>
      <EditGroupModal
        open={editModalIsOpen}
        setOpen={setEditModalIsOpen}
        onClose={() => setEditModalIsOpen(false)}
        group={group}
        // offices={offices}
        mode={editModalType}
      />
      <Popup
        open={removeUsersAlertIsOpen}
        onClose={closeRemoveUsersAlert}
        onConfirm={handleRemoveUser}
        buttonLabels={{
          confirm: t('groupProfile.removeMemberModal.confirm'),
          cancel: t('groupProfile.removeMemberModal.cancel'),
        }}
        icon={removeImg}>
        <h4>{t('groupProfile.removeMemberModal.title')}</h4>
        <p style={{ fontSize: '16px' }}>
          {usersForRemoval.length === 1
            ? t('groupProfile.removeMemberModal.description')
            : t('groupProfile.removeMemberModal.pluralDescription', { membersCount: usersForRemoval.length })}
        </p>
      </Popup>
      <PageHeader>
        <StyledActionsBox>
          <IconButton onClick={handleAddGroupMembersModalOpen}>
            <FontAwesomeIcon icon={faUserPlus as IconProp} fontSize={18} color={theme.palette.black} />
          </IconButton>
          <IconButton onClick={handleEditGroupModalOpen}>
            <FontAwesomeIcon icon={faPenToSquare as IconProp} fontSize={18} color={theme.palette.black} />
          </IconButton>
          <IconButton onClick={handleDeleteGroupModalOpen}>
            <FontAwesomeIcon icon={faTrash as IconProp} fontSize={18} color={theme.palette.black} />
          </IconButton>
        </StyledActionsBox>
      </PageHeader>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title={group?.name + ' ' + t('groupProfile.groupDetails')} />
          <Box paddingTop={2}>
            <StyledDetailBox>
              <FontAwesomeIcon icon={faUsers as IconProp} fontSize={18} />
              <StyledKeyTypography color={'inherit'}>{t('groupProfile.memberCount')}</StyledKeyTypography>
              <Typography color={'inherit'}>{group?.members?.count}</Typography>
            </StyledDetailBox>
            <StyledDivider orientation="horizontal" />
          </Box>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title={t('groupProfile.groupScore')} />
          <StyledScoreMeterBox>
            <GaugeScoremeter score={group?.awareness_score || 0} hideTitle />
          </StyledScoreMeterBox>
        </Card>
      </Grid>
      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader title={t('groupProfile.groupMembers')} />
          <StyledSearchBox>
            <SimpleSearchBar onChange={handleSearch} debounceTime={0} />
          </StyledSearchBox>
          <PaginatingTable
            multiSelectBarProps={{
              label: 'groupProfile.usersTable.user_count',
              actions: multiSelectActions,
            }}
            noResultsTextId={'groupProfile.usersTable.noResults'}
            columns={columns}
            data={members?.results ?? []}
            fetchData={fetchData}
            loading={isLoading || isFetching}
            pageCount={pageCount}
            count={members?.total}
            onRowClick={handleNavigateToMemberProfile}
            noResultsContent={<NoResults membersCount={members?.total} hasSearch={!!queryFilters?.first_name} />}
          />
        </Card>
      </Grid>
    </LayoutPageContent>
  )
}

const NoResults: FC<{ membersCount?: number; hasSearch?: boolean }> = ({ membersCount, hasSearch = false }) => {
  const { t } = useTranslation()
  return (
    <InTableMessageWithAnimation>
      <p>{t(!membersCount ? (!hasSearch ? 'groupProfile.noMembers' : 'groupProfile.noMembersSearch') : '')}</p>
    </InTableMessageWithAnimation>
  )
}

const StyledActionsBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}))

const StyledDivider = styled(Divider)(() => ({
  margin: '0px 15px',
  borderBottomWidth: '3px',
  borderColor: '#E0EDEF',
}))

const StyledKeyTypography = styled(Typography)(() => ({
  fontWeight: theme.typography.fontWeightBold,
}))

const StyledDetailBox = styled(Box)(() => ({
  display: 'flex',
  padding: '12px 26px',
  gap: '12px',
  alignItems: 'center',
}))

const StyledScoreMeterBox = styled(Box)(() => ({
  justifyContent: 'center',
}))

const StyledSearchBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
}))

export default GroupProfile
